import { useRouter } from 'next/router';
import { defineMessages, useIntl } from 'react-intl';

import type { ProductCardSpecificProps } from '@packages/modules/src/ProductCard/types';
import { mergeQueryParameters } from '@packages/shared/src/utils/mergeQueryParameters';

import { useDeviceType } from '@packages/shared';
import type { ProductVariation } from '../../api/rest/types';
import type { SearchProductCardProps } from './SearchProductCard';
import type { DesktopDisplayMode } from '../../types';

export const priceLabelMessages = defineMessages({
  msrp: {
    id: 'price.label.msrp',
    defaultMessage: 'UVP',
  },
  oldPrice: {
    id: 'price.label.oldPrice',
    defaultMessage: 'statt',
  },
});

const getVariationImage = (
  imageUrl: string,
  highlightArticleImageUrl: string | undefined,
  displayMode: DesktopDisplayMode | undefined,
  isDesktop: boolean,
): string => {
  // if there is no highlight image url, show normal image
  if (!highlightArticleImageUrl) return imageUrl;

  // show regular image if product card is in "Mobile Listenansicht" or "Tablet Listenansicht" mode
  return !isDesktop && displayMode === 'moreInformation' ? imageUrl : highlightArticleImageUrl;
};

export const useVariationDataForProductCard = (
  product: SearchProductCardProps['product'],
  variationIndex: number,
  fullVariation: ProductVariation,
  useUnitPriceSpacer?: boolean,
  displayMode?: DesktopDisplayMode,
): ProductCardSpecificProps => {
  const { formatMessage } = useIntl();

  const { query } = useRouter();

  const { isDesktop } = useDeviceType();

  const {
    sku,
    brand,
    title,
    variations,
    masterSku,
    productPosition,
    mkz,
    avgRating,
    ...styleProperties
  } = product;

  // remove 'categories' and 'searchquery' from query object to avoid passing them to URL
  const { categories, searchquery, ...filteredQuery } = query;

  const productUrl = mergeQueryParameters(fullVariation.productUrl, filteredQuery);

  const uvpLabelString =
    (fullVariation.oldPrice?.uvp && formatMessage(priceLabelMessages.msrp)) ||
    (fullVariation.oldPrice && formatMessage(priceLabelMessages.oldPrice)) ||
    undefined;

  return {
    ...styleProperties,
    sku: fullVariation.sku,
    brand,
    title,
    colors: variations.map((variant, index) => ({
      name: variant.variationName,
      color: variant.patch.rgb ? `#${variant.patch.rgb}` : undefined,
      image: variant.patch.url,
      active: index === variationIndex,
    })),
    availability: fullVariation.availability,
    variationName: fullVariation.variationName,
    productLink: productUrl,
    image: getVariationImage(
      fullVariation.imageUrl,
      fullVariation.highlightArticleImageUrl,
      displayMode,
      isDesktop,
    ),
    price: {
      uvpLabel: uvpLabelString,
      currency: fullVariation.price?.currency,
      price: fullVariation.price.value * 100,
      oldPrice: fullVariation.oldPrice?.value ? fullVariation.oldPrice.value * 100 : undefined,
      discountType: fullVariation.price.savingType?.toLowerCase() as
        | 'currency'
        | 'percent'
        | undefined,
      discountInfoLabel: '-',
      isStartPrice: fullVariation.isStartPrice,
      unit: fullVariation.pricePerUnit?.unit,
      unitPrice: fullVariation.pricePerUnit?.value
        ? fullVariation.pricePerUnit.value * 100
        : undefined,
      saving: fullVariation.price.saving,
      useUnitPriceSpacer,
    },
  };
};
